import globalMethods from "@/globalMethods";
import {URLS} from "@/constants";
import {notify} from "@/plugins/notification";
import i18n from "@/i18n";


const DEFAULT_SETTINGS = {
    ai_concierge_enabled: false,
    ai_concierge_prompt: "",
    ai_concierge_hours: {timezone: "", weekly: {}, exceptions: {}},
    ai_concierge_prompt_version: 0,
    ai_concierge_daily_token_limit: 100000,
    ai_concierge_can_create_appointments: false,
};


export default {
    namespaced: true,
    state: {
        settings: {...DEFAULT_SETTINGS},
        sessions: [],
        sessionMessages: {},  // keyed by session id
        unanswered: [],
    },
    mutations: {
        setSettings(state, data) {
            state.settings = {...DEFAULT_SETTINGS, ...data};
        },
        setSessions(state, data) {
            state.sessions = data;
        },
        setSessionMessages(state, {sessionId, data}) {
            state.sessionMessages = {...state.sessionMessages, [sessionId]: data};
        },
        setUnanswered(state, data) {
            state.unanswered = data;
        },
    },
    actions: {
        async getSettings({commit, rootState}) {
            const {personalData, company} = rootState;
            const url = URLS.AI_CONCIERGE.SETTINGS_URL;
            const options = {
                method: "GET",
                headers: {
                    role: personalData.role,
                    companyId: company.companyData?.token,
                },
                credentials: "include",
            };
            const successfulHandler = (data) => {
                commit("setSettings", data.data);
                return {successfully: true};
            };
            return globalMethods.sendRequestToGetJson({url, options, successfulHandler});
        },
        async updateSettings({commit, rootState}, payload) {
            const {personalData, company} = rootState;
            const url = URLS.AI_CONCIERGE.SETTINGS_URL;
            const options = {
                method: "PATCH",
                headers: {
                    "Content-Type": "application/json",
                    "X-CSRFToken": globalMethods.getCookie("csrftoken"),
                    role: personalData.role,
                    companyId: company.companyData?.token,
                },
                credentials: "include",
                body: JSON.stringify(payload),
            };
            const successfulHandler = (data) => {
                commit("setSettings", data.data);
                notify(i18n.global.t("AI concierge settings saved"));
                return {successfully: true};
            };
            return globalMethods.sendRequestToGetJson({url, options, successfulHandler});
        },
        async getSessions({commit, rootState}, filters = {}) {
            const {personalData, company} = rootState;
            const params = new URLSearchParams();
            if (filters.state) params.set("state", filters.state);
            if (filters.platform) params.set("platform", filters.platform);
            const query = params.toString();
            const url = query
                ? `${URLS.AI_CONCIERGE.SESSIONS_URL}?${query}`
                : URLS.AI_CONCIERGE.SESSIONS_URL;
            const options = {
                method: "GET",
                headers: {
                    role: personalData.role,
                    companyId: company.companyData?.token,
                },
                credentials: "include",
            };
            const successfulHandler = (data) => {
                commit("setSessions", data.data);
                return {successfully: true};
            };
            return globalMethods.sendRequestToGetJson({url, options, successfulHandler});
        },
        async getSessionMessages({commit, rootState}, sessionId) {
            const {personalData, company} = rootState;
            const url = URLS.AI_CONCIERGE.SESSION_URL.replace("<ID>", sessionId);
            const options = {
                method: "GET",
                headers: {
                    role: personalData.role,
                    companyId: company.companyData?.token,
                },
                credentials: "include",
            };
            const successfulHandler = (data) => {
                commit("setSessionMessages", {sessionId, data: data.data});
                return {successfully: true, data: data.data};
            };
            return globalMethods.sendRequestToGetJson({url, options, successfulHandler});
        },
        async getUnanswered({commit, rootState}, {onlyUnresolved = true} = {}) {
            const {personalData, company} = rootState;
            const url = `${URLS.AI_CONCIERGE.UNANSWERED_URL}?only_unresolved=${onlyUnresolved}`;
            const options = {
                method: "GET",
                headers: {
                    role: personalData.role,
                    companyId: company.companyData?.token,
                },
                credentials: "include",
            };
            const successfulHandler = (data) => {
                commit("setUnanswered", data.data);
                return {successfully: true};
            };
            return globalMethods.sendRequestToGetJson({url, options, successfulHandler});
        },
        async resolveUnanswered({dispatch, rootState}, resolvedIds) {
            const {personalData, company} = rootState;
            const url = URLS.AI_CONCIERGE.UNANSWERED_URL;
            const options = {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "X-CSRFToken": globalMethods.getCookie("csrftoken"),
                    role: personalData.role,
                    companyId: company.companyData?.token,
                },
                credentials: "include",
                body: JSON.stringify({resolved_ids: resolvedIds}),
            };
            const successfulHandler = () => {
                notify(i18n.global.t("Marked as resolved"));
                return {successfully: true};
            };
            const result = await globalMethods.sendRequestToGetJson({url, options, successfulHandler});
            if (result?.successfully) {
                await dispatch("getUnanswered", {onlyUnresolved: true});
            }
            return result;
        },
    },
    getters: {
        unansweredCount: (state) => state.unanswered.length,
    },
};
